import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import TextField from '@mui/material/TextField';
import { useLazyQuery } from '@apollo/client';
import { AgGridReact } from 'ag-grid-react';
import { InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import { MEMBER_LIST } from '../../apis/member';
import AssignMemberPopOver from '../AssignMemberPopOver';
import deleteIconButton from './deleteMember';
import RemoveAssignment from './removeAssignment';
import SkillButton from '../skillButton';
import useLoader from '../../context/LoaderContext';
import useProjectVersionHistory from '../../context/VersionHistoryContext';
import UserPanel from '../UserPanel';
import useWorkspace from '../../context/WorkspaceContext';

const propTypes = {
  projectId: PropTypes.string,
  profiles: PropTypes.array,
};

const defaultProps = {
  projectId: '',
};

const containerStyle = { width: '100%', height: '100%' };
const gridStyle = { height: '100%', width: '100%' };

const MemberList = (props) => {
  // init
  const { projectId, profiles } = props;
  const { isLoading, setIsLoading } = useLoader();
  const { showTeamTour, setTeamTour } = useWorkspace();
  const { selectedListItem, showVersionHistoryBar, readOnlyMode, undo, redo } = useProjectVersionHistory();

  // states
  const [availableResources, setAvailableResources] = useState([]);
  const [rowData, setRowData] = useState([]);
  const [showMemberTour, setShowMemberTour] = useState(false);
  const [gridApi, setGridApi] = useState(null);

  // mutations/queries
  const [getList] = useLazyQuery(MEMBER_LIST, { fetchPolicy: 'no-cache', variables: { projectId: parseInt(projectId, 10) } });

  // constants
  const isHidden = showVersionHistoryBar && readOnlyMode && selectedListItem !== null && selectedListItem !== undefined;

  const getTeamMembersList = async () => {
    setAvailableResources(profiles);
    if (!isLoading) setIsLoading(true);
    const { data } = await getList();
    const skillCount = {};
    const busyResources = {};
    if (data && data.getProjectSkillUsers.length > 0) {
      const team = data.getProjectSkillUsers.map((row, index) => {
        let memberNameValue = '';
        let id = 0;
        let email = '';
        if (row.workspaceProfile) {
          memberNameValue = `${row.workspaceProfile.displayName}`;
          id = row.workspaceProfile.id;
          email = row.workspaceProfile?.userWorkspace?.email;
          busyResources[row.workspaceProfile.id] = row.workspaceProfile;
        } else {
          if (row.skillId in skillCount) {
            skillCount[row.skillId] += 1;
          } else {
            skillCount[row.skillId] = 1;
          }
          memberNameValue = skillCount[row.skillId];
        }
        return {
          id,
          projectSkillId: row.id,
          uId: row?.user?.id,
          alldata: row.workspaceProfile,
          emailAddress: email,
          logo: row.workspaceProfile?.logo,
          no: index,
          skillset: row.skill.name,
          memberName: memberNameValue,
          skillId: row.skillId,
        };
      });
      setAvailableResources(profiles.filter((profile) => !busyResources[profile.id]));
      setRowData(team);
    } else {
      setRowData([]);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (profiles.length) getTeamMembersList();
  }, [profiles, selectedListItem, undo, redo]);

  useEffect(() => {
    if (isLoading === false && showTeamTour === true) {
      const teamTourOptions = {
        options: {
          darkLayerPersistence: true,
        },
        tips: [
          {
            title: '👆 Add Member',
            description: 'Add more skills to your project from here',
            selector: '.add-member',
            x: 15,
            y: 70,
            offx: 0,
            offy: 0,
            position: 'bottom',
          },
        ],
      };
      if (rowData && rowData.length > 0) {
        const tip1 = {
          title: '👆 List of team members by skill set',
          description: `These are the team members who are added to the project.  
              Estimate columns will appear on the epics list corresponding to the skills here`,
          selector: '.search-input',
          x: 40,
          y: 300,
          offx: 0,
          offy: 0,
          position: 'bottom',
        };
        teamTourOptions.tips.unshift(tip1);
        const tip2 = {
          title: '👈 Assign Member',
          description:
            'Skills represent placeholders by default, but you can assign specific team members here',
          selector: '.search-input',
          x: 180,
          y: 290,
          offx: 0,
          offy: 0,
          position: 'right',
        };
        teamTourOptions.tips.push(tip2);
        if (showMemberTour === true) {
          teamTourOptions.tips.splice(1, 1);
        }
      }
      try {
        window.ProductTourJS.init(teamTourOptions);
        window.ProductTourJS.start();
        setShowMemberTour(true);
        if (teamTourOptions.tips.length !== 1) {
          setTeamTour(false);
        }
      } catch (e) {
        console.error(e);
      }
    }
  }, [isLoading, rowData, selectedListItem]);

  const memberNameCellRender = (params) => {
    let renderedOutput = null;
    if (typeof params.value === 'string') {
      renderedOutput = (
        <div className="inline-flex-center user-name-title">
          <span className="member-name">
            <UserPanel user={params} />
          </span>
        </div>
      );
    } else {
      renderedOutput = (
        <div className="inline-flex-center">
          <span className="member-name">{`${params.data.skillset} ${params.value}`}</span>
        </div>
      );
    }
    return renderedOutput;
  };

  const assignRemoveCellRender = (params) => {
    let renderedOutput = null;
    if (typeof params.value === 'string') {
      renderedOutput = (
        <div>
          <span>
            <RemoveAssignment
              name={params.value}
              tableId={params.data.projectSkillId}
              getTeamMembers={getTeamMembersList}
              skillName={params.data.skillset}
              projectId={projectId}
            />
          </span>
        </div>
      );
    } else {
      renderedOutput = (
        <div>
          <span>
            <AssignMemberPopOver
              skillId={params.data.skillId}
              tableId={params.data.projectSkillId}
              techMember={availableResources || []}
              getTeamMembers={getTeamMembersList}
            />
          </span>
        </div>
      );
    }
    return renderedOutput;
  };

  const deleteHandler = () => {
    getTeamMembersList();
  };

  const columnDefs = [
    {
      headerName: 'Id',
      field: 'id',
      hide: true,
    },
    {
      headerName: 'No',
      field: 'no',
      cellRenderer: deleteIconButton,
      width: 122,
      cellRendererParams: {
        deleteHandler,
      },
      valueGetter: 'node.rowIndex + 1',
      suppressSizeToFit: true,
      headerClass: 'first-header-cell',
      cellClass: 'first-cell',
    },
    {
      headerName: 'Skillset',
      field: 'skillset',
      width: 210,
    },
    {
      headerName: 'Member Name',
      field: 'memberName',
      cellRenderer: memberNameCellRender,
      width: 350,
    },
    {
      headerName: '',
      field: 'memberName',
      cellRenderer: assignRemoveCellRender,
      width: 350,
    },
  ];

  const onFilterTextBoxChanged = (e) => {
    const dataFilter = e.target.value;
    gridApi.setQuickFilter(dataFilter);
  };
  const defaultColDef = useMemo(
    () => ({
      resizable: false,
      suppressMovable: true,
    }),
    [],
  );
  return (
    <>
      <div className="page-header" style={{ padding: 0, marginTop: isHidden ? 20 : 0 }}>
        <div style={{ display: isHidden ? 'none' : '', paddingRight: 28 }} className="header-layout">
          <div className="top-search-btn search-btn-project-detail">
            <TextField
              className="search-input"
              type="search"
              onChange={onFilterTextBoxChanged}
              placeholder="Search"
              InputProps={{
                startAdornment: (
                  <InputAdornment style={{ color: 'rgba(0, 0, 0, 0.4)' }} position="start">
                    <SearchIcon className="search-icon" />
                  </InputAdornment>
                ),
              }}
            />
          </div>
          <SkillButton gridApi={gridApi} getTeamMembers={getTeamMembersList} />
        </div>
      </div>
      <div style={containerStyle}>
        <div style={{ height: '100%', width: '100%', boxSizing: 'border-box' }}>
          {rowData.length > 0 && (
            <div style={gridStyle} className="ag-theme-alpine">
              <AgGridReact
                className="ag-theme-custom-react common-table-grid team-table-grid"
                gridOptions={{
                  suppressNoRowsOverlay: true,
                  rowHeight: 40,
                  headerHeight: 40,
                  defaultColDef: {
                    resizable: false,
                  },
                }}
                rowBuffer
                rowData={rowData}
                columnDefs={columnDefs}
                domLayout="autoHeight"
                animateRows
                onGridReady={(gridParams) => {
                  setGridApi(gridParams.api);
                }}
                debounceVerticalScrollbar
                defaultColDef={defaultColDef}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

MemberList.propTypes = propTypes;
MemberList.defaultProps = defaultProps;
export default MemberList;
